import { PlusIcon, XIcon } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  SurchargeConditionChildType,
  SurchargeConditionProperty,
  SurchargeConditionPropertyType,
} from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/core/form';
import { Input } from '@/components/core/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';
import { SwitchFormField } from '@/components/forms/switch-form-field';
import { TagsInput } from '@/components/tags-input';

import {
  emptyConditionRow,
  isArrayOperator,
  operatorsForProperty,
  propertyType,
  surchargeConditionChildTypeTitle,
  surchargeConditionOperatorTitle,
  surchargeConditionPropertyTitle,
} from './surcharge-applicability';
import type { SurchargeConditionsForm } from './surcharge-applicability';

/** The form must expose its applicability section under this key for the builder to bind to it. */
type ConditionsFieldValues = FieldValues & { conditions: SurchargeConditionsForm };

type RowProps<TFieldValues extends ConditionsFieldValues> = {
  control: Control<TFieldValues>;
  index: number;
  onRemove: () => void;
  removable: boolean;
};

/**
 * One condition row: property select → operator select (constrained to the property's value type)
 * → value input typed by the property (number / text / chips for IN/NOT_IN). Mirrors the Automation
 * rule-card condition row, rebuilt on shadcn/RHF rather than the legacy Ant builder.
 */
function ConditionRow<TFieldValues extends ConditionsFieldValues>({
  control,
  index,
  onRemove,
  removable,
}: RowProps<TFieldValues>) {
  const { watch, setValue } = useFormContext<ConditionsFieldValues>();
  const property = watch(`conditions.rows.${index}.property`);
  const operator = watch(`conditions.rows.${index}.operator`);

  const operators = operatorsForProperty(property);
  const isNumber = propertyType(property) === SurchargeConditionPropertyType.NUMBER;
  const usesChips = isArrayOperator(operator);

  const propertyName = `conditions.rows.${index}.property` as FieldPath<TFieldValues>;
  const operatorName = `conditions.rows.${index}.operator` as FieldPath<TFieldValues>;
  const valueName = `conditions.rows.${index}.value` as FieldPath<TFieldValues>;
  const valuesName = `conditions.rows.${index}.values` as FieldPath<TFieldValues>;

  return (
    <div className="flex flex-wrap items-start gap-2">
      <FormField
        control={control}
        name={propertyName}
        render={({ field }) => (
          <FormItem className="min-w-44 flex-1">
            <Select
              value={field.value}
              onValueChange={(next) => {
                field.onChange(next);
                // The operator set depends on the property's type; reset to the first valid operator
                // and clear the value so a stale string/number can't leak into the new shape.
                const nextOperators = operatorsForProperty(next as SurchargeConditionProperty);
                setValue(`conditions.rows.${index}.operator`, nextOperators[0]);
                setValue(`conditions.rows.${index}.value`, '');
                setValue(`conditions.rows.${index}.values`, []);
              }}
            >
              <FormControl>
                <SelectTrigger className="w-full" aria-label="Condition property">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.values(SurchargeConditionProperty).map((value) => (
                  <SelectItem key={value} value={value}>
                    {surchargeConditionPropertyTitle[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={operatorName}
        render={({ field }) => (
          <FormItem className="min-w-44 flex-1">
            <Select
              value={field.value}
              onValueChange={(next) => {
                field.onChange(next);
                // Switching between scalar and array operators swaps which value field is live; clear
                // both so the inactive one never contributes a stale value on submit.
                setValue(`conditions.rows.${index}.value`, '');
                setValue(`conditions.rows.${index}.values`, []);
              }}
            >
              <FormControl>
                <SelectTrigger className="w-full" aria-label="Condition operator">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {operators.map((value) => (
                  <SelectItem key={value} value={value}>
                    {surchargeConditionOperatorTitle[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {usesChips ? (
        <FormField
          control={control}
          name={valuesName}
          render={({ field }) => (
            <FormItem className="min-w-44 flex-1">
              <FormControl>
                <TagsInput
                  aria-label="Condition values"
                  placeholder="Add value, press Enter"
                  value={Array.isArray(field.value) ? field.value : []}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <FormField
          control={control}
          name={valueName}
          render={({ field }) => (
            <FormItem className="min-w-44 flex-1">
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  type={isNumber ? 'number' : 'text'}
                  step={isNumber ? 'any' : undefined}
                  aria-label="Condition value"
                  placeholder="Value"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="mt-0.5"
        aria-label="Remove condition"
        onClick={onRemove}
        disabled={!removable}
      >
        <XIcon />
      </Button>
    </div>
  );
}

type Props<TFieldValues extends ConditionsFieldValues> = {
  control: Control<TFieldValues>;
};

/**
 * Structured applicability builder rendered inside the create-surcharge form. A switch enables the
 * section; when on, a "match All / Any" select heads a list of repeatable condition rows. Produces a
 * `conditions` form value that `buildApplicability` converts to a `SurchargeConditionDTO` on submit.
 *
 * Single-level for v1 (the four properties + All/Any cover the seeded rules); the DTO is recursive,
 * so nesting can follow without reshaping the output.
 */
export function SurchargeConditionsBuilder<TFieldValues extends ConditionsFieldValues>({
  control,
}: Props<TFieldValues>) {
  const { watch } = useFormContext<ConditionsFieldValues>();
  const enabled = watch('conditions.enabled');

  const { fields, append, remove } = useFieldArray({
    control: control as unknown as Control<ConditionsFieldValues>,
    name: 'conditions.rows',
  });

  const childTypeName = 'conditions.childConditionType' as FieldPath<TFieldValues>;

  return (
    <div className="grid gap-3">
      <SwitchFormField
        control={control}
        name={'conditions.enabled' as FieldPath<TFieldValues>}
        label="Conditions"
        description="Apply this surcharge only when the shipment matches these conditions."
      />
      {enabled && (
        <div className="grid gap-3 rounded-lg border p-3">
          <FormField
            control={control}
            name={childTypeName}
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-2 space-y-0">
                <FormLabel className="font-normal text-muted-foreground">Match</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-28" aria-label="Match type">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(SurchargeConditionChildType).map((value) => (
                      <SelectItem key={value} value={value}>
                        {surchargeConditionChildTypeTitle[value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormLabel className="font-normal text-muted-foreground">
                  of the following conditions
                </FormLabel>
              </FormItem>
            )}
          />
          <div className="grid gap-2">
            {fields.map((row, index) => (
              <ConditionRow
                key={row.id}
                control={control}
                index={index}
                removable={fields.length > 1}
                onRemove={() => remove(index)}
              />
            ))}
          </div>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append(emptyConditionRow())}
            >
              <PlusIcon />
              Add condition
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
